/**
 * THE STATEMENT OF APPLICABILITY, BUILT INSIDE THE ASSESSMENT.
 *
 * This panel appears on each of the four Annex A questions of the ISO 27001
 * assessment, and it is the primary way an SoA gets written. The separate
 * /soa screen still exists for an advisor who wants all 93 rows in one place,
 * but nobody should have to go there to produce the document.
 *
 * ---------------------------------------------------------------------------
 *  THE PERSON THIS IS WRITTEN FOR
 * ---------------------------------------------------------------------------
 * An IT manager who knows every system in the building and has never read ISO
 * 27001. "Is A.5.7 Threat Intelligence applicable?" is not a hard question for
 * them — it is an unanswerable one, because it is in a language nobody gave
 * them. So the panel never asks it. It asks:
 *
 *   1. Up to nine yes/no questions about facts of their organisation — "do you
 *      write any software of your own?", "do you occupy premises you control?"
 *      Each one settles several controls at once, and each is asked once for
 *      the whole document rather than once per control.
 *   2. Then it offers the remaining controls as one confirmation: "these apply
 *      to essentially every organisation — confirm", with a justification they
 *      can read and edit before they press it.
 *
 * That is the whole path. Nine answers and two confirmations produce a complete
 * Statement of Applicability, and every control still carries a justification.
 *
 * ---------------------------------------------------------------------------
 *  WHAT IS A DECISION AND WHAT IS NOT
 * ---------------------------------------------------------------------------
 * Nothing on this screen writes an applicability decision until a person
 * presses something. Answering "no, we write no software" does NOT silently
 * exclude six controls — it produces a proposal, listed by name with the
 * sentence that will be recorded, which the user then confirms. The difference
 * between a suggestion someone accepted and a default someone never saw is the
 * difference between a document and a forgery, and it is the reason this panel
 * has a confirm step that could otherwise be optimised away.
 *
 * Two rules are enforced on the server as well as here, because a UI rule is
 * not a rule:
 *   * exclusions are never recorded in bulk (SOA_BULK_EXCLUSION_REFUSED);
 *   * an exclusion cannot be stored without a reason (SOA_EXCLUSION_NEEDS_REASON).
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  SOA_ASK_GROUP_BY_KEY,
  SOA_CONTROL_GUIDANCE,
  SOA_DEFAULT_INCLUSION_JUSTIFICATION,
  SOA_EXCLUSION_GROUNDS,
  SOA_EXCLUSION_JUSTIFICATION,
  type SoaExclusionGround,
} from '@opencomply/shared';
import { api } from '../lib/api';
import { useApiError } from '../lib/useApiError';
import { cn } from '../lib/cn';
import { Button, Pill, Skeleton, ErrorNotice, ProgressBar } from './ui';
import type { SoaDocument, SoaRow } from '../lib/soaTypes';

// ---------------------------------------------------------------------------
//  Strings — inline English defaults, the EVIDENCE_STRINGS pattern.
// ---------------------------------------------------------------------------

export const SOA_FLOW_STRINGS = {
  'soaFlow.title': 'Statement of Applicability',
  'soaFlow.intro':
    'ISO 27001 requires you to say, for every control in this theme, whether it applies to you and why. Answer the questions below in your own terms and the document writes itself — you never have to read the standard.',
  'soaFlow.progress': '{{decided}} of {{total}} controls decided across the whole document',
  'soaFlow.themeProgress': '{{decided}} of {{total}} decided in this theme',
  'soaFlow.loadFailed': 'Could not load the applicability decisions.',
  'soaFlow.questionsTitle': 'A few questions about your organisation',
  'soaFlow.questionsHint':
    'Each answer settles several controls at once. Answer once and it holds for the whole document.',
  'soaFlow.yes': 'Yes',
  'soaFlow.no': 'No',
  'soaFlow.affects_one': 'Settles {{count}} control in this theme',
  'soaFlow.affects_other': 'Settles {{count}} controls in this theme',
  'soaFlow.answeredYes': 'You said yes, so these stay applicable.',
  'soaFlow.pendingProposal': 'Review the proposal below before anything is recorded.',

  // Step 2 — the proposal
  'soaFlow.proposeExcludeTitle': 'Proposed exclusions — confirm before anything is recorded',
  'soaFlow.proposeExcludeHint':
    'Nothing has been written yet. Read what will be recorded against each control, then confirm. Each one is recorded separately; this product never excludes controls in bulk, because a sweep of exclusions is the first thing an auditor looks for.',
  'soaFlow.confirmExclusion': 'Record this exclusion',
  'soaFlow.confirmRestTitle': 'The remaining controls in this theme',
  'soaFlow.confirmRestHint':
    'These apply to essentially every organisation. Read the justification that will be recorded, edit it if you want to, then confirm them together.',
  'soaFlow.confirmRest_one': 'Confirm {{count}} control as applicable',
  'soaFlow.confirmRest_other': 'Confirm {{count}} controls as applicable',
  'soaFlow.inclusionJustification': 'Justification that will be recorded',
  'soaFlow.allDecided': 'Every control in this theme has a decision.',
  'soaFlow.applicable': 'Applicable',
  'soaFlow.excluded': 'Excluded',
  'soaFlow.exclude': 'This does not apply to us',
  'soaFlow.excludeReason': 'Why does it not apply?',
  'soaFlow.excludeReasonOther': 'Say why, in your own words',
  'soaFlow.excludeConfirm': 'Record the exclusion',
  'soaFlow.cancel': 'Cancel',
  'soaFlow.change': 'Change',
  'soaFlow.willRecord': 'This will be recorded:',

  // Grounds
  'soaFlow.ground.NO_IN_HOUSE_DEVELOPMENT': 'We write no software of our own',
  'soaFlow.ground.NO_OUTSOURCED_DEVELOPMENT': 'We do not have software built for us',
  'soaFlow.ground.NO_TEST_ENVIRONMENTS': 'We have no test or development environments',
  'soaFlow.ground.NO_OWN_PREMISES': 'We occupy no premises of our own',
  'soaFlow.ground.NO_ON_PREMISES_INFRASTRUCTURE': 'We run no IT equipment on our own premises',
  'soaFlow.ground.NO_REMOVABLE_MEDIA': 'We do not use removable media',
  'soaFlow.ground.NO_REMOTE_WORKING': 'Nobody works away from our premises',
  'soaFlow.ground.NO_CLOUD_SERVICES': 'We use no cloud services',
  'soaFlow.ground.NO_SUPPLIERS': 'We use no external suppliers',
  'soaFlow.ground.NO_PAYMENT_CARD_DATA': 'We handle no payment card data',
  'soaFlow.ground.NO_INDUSTRIAL_CONTROL_SYSTEMS': 'We have no industrial control or OT systems',
  'soaFlow.ground.NO_PERSONAL_DATA_BEYOND_EMPLOYEES': 'We hold no personal data beyond employee records',
  'soaFlow.ground.OUTSIDE_SCOPE_BOUNDARY': 'It falls outside our ISMS scope',
  'soaFlow.ground.OTHER': 'Something else',
  /*
    A model exclusion, shown as a PLACEHOLDER on the one box in this panel
    whose text becomes the stored justification. Grey, gone on the first
    keystroke, never submitted — an empty box still records nothing.
  */
  'soaFlow.excludeExample': 'For example — {{example}}',
  'soaFlow.excludeExampleShape':
    'For example — name what about your organisation puts this control outside the scope, and say what would bring it back in.',

  // Finishing
  'soaFlow.deliverableTitle': 'Your Statement of Applicability',
  'soaFlow.deliverableIncomplete':
    '{{remaining}} controls across the whole document still have no decision. The export says so plainly rather than filling them in — an SoA that asserts a decision you never took is worse than an incomplete one.',
  'soaFlow.deliverableComplete':
    'All 93 Annex A controls have a decision. The document is ready to export.',
  'soaFlow.openFullList': 'Review all 93 in one list',
} as const;

export type SoaFlowKey = keyof typeof SOA_FLOW_STRINGS;
const PLURAL_KEYS = ['soaFlow.affects', 'soaFlow.confirmRest'] as const;
type SoaFlowPluralKey = (typeof PLURAL_KEYS)[number];

export function useSoaFlowStrings() {
  const { t } = useTranslation();
  const tx = (key: SoaFlowKey, options?: Record<string, unknown>) =>
    t(key, { defaultValue: SOA_FLOW_STRINGS[key], ...options });
  const txn = (key: SoaFlowPluralKey, count: number) =>
    t(key, {
      count,
      defaultValue: SOA_FLOW_STRINGS[(count === 1 ? `${key}_one` : `${key}_other`) as SoaFlowKey],
    });
  return { tx, txn };
}

// ---------------------------------------------------------------------------

/** Yes / No / not yet answered, per shared ask-group. Held in the page only. */
type AskAnswers = Record<string, 'YES' | 'NO' | undefined>;

export function SoaAssessmentPanel({
  /** "A.5" … "A.8" — derived from the question code by the caller. */
  theme,
  readOnly,
}: {
  theme: string;
  readOnly: boolean;
}) {
  const { tx, txn } = useSoaFlowStrings();
  const describeError = useApiError();
  const queryClient = useQueryClient();

  const [answers, setAnswers] = useState<AskAnswers>({});
  const [inclusionText, setInclusionText] = useState(SOA_DEFAULT_INCLUSION_JUSTIFICATION);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['soa'],
    queryFn: () => api.get<SoaDocument>('/soa'),
  });

  const apply = (next: SoaDocument) => {
    queryClient.setQueryData(['soa'], next);
    setError(null);
  };
  const fail = (err: unknown) => setError(describeError(err));

  const patchOne = useMutation({
    mutationFn: ({ controlId, patch }: { controlId: string; patch: Record<string, unknown> }) =>
      api.patch<SoaDocument>(`/soa/controls/${encodeURIComponent(controlId)}`, patch),
    onSuccess: apply,
    onError: fail,
  });

  const confirmMany = useMutation({
    mutationFn: ({ controlIds, decision }: { controlIds: string[]; decision: Record<string, unknown> }) =>
      api.post<SoaDocument>('/soa/bulk', { controlIds, decision }),
    onSuccess: apply,
    onError: fail,
  });

  const rows = useMemo(
    () => (data?.rows ?? []).filter((r) => r.theme === theme),
    [data?.rows, theme],
  );

  /** The ask groups that actually bear on this theme, in their declared order. */
  const groups = useMemo(() => {
    const keys = new Set(rows.map((r) => r.askGroup).filter((k): k is string => !!k));
    return [...keys].map((key) => SOA_ASK_GROUP_BY_KEY[key]!).filter(Boolean);
  }, [rows]);

  if (isLoading) return <Skeleton className="h-32" />;
  if (isError || !data) {
    return <ErrorNotice message={tx('soaFlow.loadFailed')} onRetry={() => void refetch()} />;
  }
  if (rows.length === 0) return null;

  const themeDecided = rows.filter((r) => r.applicability !== 'UNDECIDED').length;
  const undecided = rows.filter((r) => r.applicability === 'UNDECIDED');

  /** Controls a "no" answer proposes excluding — proposals only, never written. */
  const proposedExclusions = undecided.filter(
    (r) => r.askGroup && answers[r.askGroup] === 'NO',
  );

  /**
   * The long tail: undecided, and nothing is proposing an exclusion for them.
   * A control whose question has not been answered yet is deliberately left out
   * — confirming it as applicable before its question is answered would be
   * recording a decision the user has not got to.
   */
  const confirmable = undecided.filter(
    (r) => !r.askGroup || answers[r.askGroup] === 'YES',
  );

  const busy = patchOne.isPending || confirmMany.isPending;

  return (
    <section className="rounded-lg border border-line p-4 space-y-5">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-ink">{tx('soaFlow.title')}</h3>
          <Link to="/soa" className="text-xs text-brand hover:underline">
            {tx('soaFlow.openFullList')}
          </Link>
        </div>
        <p className="text-xs text-ink-secondary leading-relaxed">{tx('soaFlow.intro')}</p>
        <ProgressBar
          value={Math.round((data.summary.decided / data.summary.total) * 100)}
          label={tx('soaFlow.progress', {
            decided: data.summary.decided,
            total: data.summary.total,
          })}
        />
        <p className="text-xs text-ink-muted">
          {tx('soaFlow.themeProgress', { decided: themeDecided, total: rows.length })}
        </p>
      </header>

      {error && <ErrorNotice message={error} />}

      {/* --- Step 1: the questions --------------------------------------- */}
      {!readOnly && groups.length > 0 && undecided.length > 0 && (
        <div className="space-y-3">
          <div>
            <h4 className="text-xs font-semibold text-ink uppercase tracking-wide">
              {tx('soaFlow.questionsTitle')}
            </h4>
            <p className="text-xs text-ink-muted mt-0.5">{tx('soaFlow.questionsHint')}</p>
          </div>
          {groups.map((group) => {
            const affected = undecided.filter((r) => r.askGroup === group.key);
            if (affected.length === 0) return null;
            const answer = answers[group.key];
            return (
              <div key={group.key} className="rounded-md border border-line p-3">
                <p className="text-sm text-ink">{group.ask}</p>
                <p className="text-xs text-ink-muted mt-1">
                  {txn('soaFlow.affects', affected.length)}
                </p>
                <div className="flex gap-2 mt-2">
                  {(['YES', 'NO'] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={answer === value}
                      onClick={() => setAnswers((a) => ({ ...a, [group.key]: value }))}
                      className={cn(
                        'px-3 py-1.5 rounded-md text-xs border transition-colors',
                        answer === value
                          ? 'bg-brand text-white border-brand'
                          : 'border-line text-ink-secondary hover:border-brand',
                      )}
                    >
                      {value === 'YES' ? tx('soaFlow.yes') : tx('soaFlow.no')}
                    </button>
                  ))}
                </div>
                {answer === 'YES' && (
                  <p className="text-xs text-good mt-2">{tx('soaFlow.answeredYes')}</p>
                )}
                {answer === 'NO' && (
                  <p className="text-xs text-ink-secondary mt-2">
                    {group.noMeans} {tx('soaFlow.pendingProposal')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* --- Step 2: proposed exclusions, confirmed one at a time ---------- */}
      {!readOnly && proposedExclusions.length > 0 && (
        <div className="space-y-2">
          <div>
            <h4 className="text-xs font-semibold text-ink uppercase tracking-wide">
              {tx('soaFlow.proposeExcludeTitle')}
            </h4>
            <p className="text-xs text-ink-muted mt-0.5">{tx('soaFlow.proposeExcludeHint')}</p>
          </div>
          {proposedExclusions.map((row) => {
            const group = SOA_ASK_GROUP_BY_KEY[row.askGroup!]!;
            const sentence = SOA_EXCLUSION_JUSTIFICATION[group.ground];
            return (
              <div key={row.controlId} className="rounded-md border border-line p-3">
                <p className="text-sm text-ink">
                  <span className="font-mono text-xs text-ink-muted mr-2">{row.code}</span>
                  {row.title}
                </p>
                <p className="text-xs text-ink-muted mt-1">{tx('soaFlow.willRecord')}</p>
                <p className="text-xs text-ink-secondary mt-0.5 italic">{sentence}</p>
                <Button
                  size="sm"
                  className="mt-2"
                  disabled={busy}
                  onClick={() =>
                    patchOne.mutate({
                      controlId: row.controlId,
                      patch: { applicability: 'EXCLUDED', exclusionGround: group.ground },
                    })
                  }
                >
                  {tx('soaFlow.confirmExclusion')}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* --- Step 3: confirm the rest as applicable ----------------------- */}
      {!readOnly && confirmable.length > 0 && (
        <div className="space-y-2">
          <div>
            <h4 className="text-xs font-semibold text-ink uppercase tracking-wide">
              {tx('soaFlow.confirmRestTitle')}
            </h4>
            <p className="text-xs text-ink-muted mt-0.5">{tx('soaFlow.confirmRestHint')}</p>
          </div>
          <ul className="text-xs text-ink-secondary grid sm:grid-cols-2 gap-x-4 gap-y-1">
            {confirmable.map((row) => (
              <li key={row.controlId}>
                <span className="font-mono text-ink-muted mr-1.5">{row.code}</span>
                {SOA_CONTROL_GUIDANCE[row.code]?.about ?? row.title}
              </li>
            ))}
          </ul>
          <label className="block">
            <span className="oc-label">{tx('soaFlow.inclusionJustification')}</span>
            <textarea
              className="oc-input min-h-16"
              value={inclusionText}
              onChange={(e) => setInclusionText(e.target.value)}
            />
          </label>
          <Button
            variant="primary"
            size="sm"
            disabled={busy || !inclusionText.trim()}
            onClick={() =>
              confirmMany.mutate({
                controlIds: confirmable.map((r) => r.controlId),
                decision: {
                  applicability: 'APPLICABLE',
                  justification: inclusionText.trim(),
                  reasons: ['RISK_ASSESSMENT'],
                },
              })
            }
          >
            {txn('soaFlow.confirmRest', confirmable.length)}
          </Button>
        </div>
      )}

      {undecided.length === 0 && (
        <p className="text-xs text-good">{tx('soaFlow.allDecided')}</p>
      )}

      {/* --- What has been decided in this theme -------------------------- */}
      <div className="space-y-1.5">
        {rows
          .filter((r) => r.applicability !== 'UNDECIDED')
          .map((row) => (
            <DecidedRow key={row.controlId} row={row} readOnly={readOnly} busy={busy}
              onPatch={(patch) => patchOne.mutate({ controlId: row.controlId, patch })} />
          ))}
      </div>

      {/* --- The deliverable ---------------------------------------------- */}
      <footer className="pt-3 border-t border-line">
        <p className="text-xs font-semibold text-ink">{tx('soaFlow.deliverableTitle')}</p>
        <p className="text-xs text-ink-secondary mt-1">
          {data.summary.undecided > 0
            ? tx('soaFlow.deliverableIncomplete', { remaining: data.summary.undecided })
            : tx('soaFlow.deliverableComplete')}
        </p>
      </footer>
    </section>
  );
}

/**
 * A control that already has a decision.
 *
 * Shown so the decision is visible and reversible in the place it was taken.
 * Exclusions get a one-at-a-time control here too — there is no path anywhere
 * in this component that excludes more than one at a time.
 */
function DecidedRow({
  row, readOnly, busy, onPatch,
}: {
  row: SoaRow;
  readOnly: boolean;
  busy: boolean;
  onPatch: (patch: Record<string, unknown>) => void;
}) {
  const { tx } = useSoaFlowStrings();
  const [excluding, setExcluding] = useState(false);
  const [ground, setGround] = useState<SoaExclusionGround>('OUTSIDE_SCOPE_BOUNDARY');
  const [otherText, setOtherText] = useState('');

  const excluded = row.applicability === 'EXCLUDED';

  return (
    <div className="flex flex-wrap items-start justify-between gap-2 text-xs border-b border-line pb-1.5">
      <div className="min-w-0">
        <span className="font-mono text-ink-muted mr-1.5">{row.code}</span>
        <span className="text-ink-secondary">{row.title}</span>
        {excluded && row.justification && (
          <p className="text-ink-muted mt-0.5 italic">{row.justification}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Pill tone={excluded ? 'neutral' : 'good'}>
          {excluded ? tx('soaFlow.excluded') : tx('soaFlow.applicable')}
        </Pill>
        {!readOnly && !excluded && !excluding && (
          <button
            type="button"
            className="text-ink-muted hover:text-brand underline"
            onClick={() => setExcluding(true)}
          >
            {tx('soaFlow.exclude')}
          </button>
        )}
        {!readOnly && excluded && (
          <button
            type="button"
            className="text-ink-muted hover:text-brand underline"
            disabled={busy}
            onClick={() => onPatch({ applicability: 'APPLICABLE' })}
          >
            {tx('soaFlow.change')}
          </button>
        )}
      </div>

      {excluding && (
        <div className="w-full rounded-md border border-line p-2 space-y-2">
          <label className="block">
            <span className="oc-label">{tx('soaFlow.excludeReason')}</span>
            <select
              className="oc-input"
              value={ground}
              onChange={(e) => setGround(e.target.value as SoaExclusionGround)}
            >
              {SOA_EXCLUSION_GROUNDS.map((value) => (
                <option key={value} value={value}>{tx(`soaFlow.ground.${value}`)}</option>
              ))}
            </select>
          </label>
          {/* OTHER has no preset sentence, so the server refuses it without
              wording. Asking for it here keeps that from being an error the
              user has to discover. */}
          {ground === 'OTHER' && (
            <label className="block">
              <span className="oc-label">{tx('soaFlow.excludeReasonOther')}</span>
              <textarea
                className="oc-input min-h-16"
                value={otherText}
                placeholder={
                  SOA_CONTROL_GUIDANCE[row.code]?.exampleExclusion
                    ? tx('soaFlow.excludeExample', {
                        example: SOA_CONTROL_GUIDANCE[row.code]!.exampleExclusion,
                      })
                    : tx('soaFlow.excludeExampleShape')
                }
                onChange={(e) => setOtherText(e.target.value)}
              />
            </label>
          )}
          {ground !== 'OTHER' && (
            <p className="text-ink-muted italic">{SOA_EXCLUSION_JUSTIFICATION[ground]}</p>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={busy || (ground === 'OTHER' && !otherText.trim())}
              onClick={() => {
                onPatch({
                  applicability: 'EXCLUDED',
                  exclusionGround: ground,
                  ...(ground === 'OTHER' ? { justification: otherText.trim() } : {}),
                });
                setExcluding(false);
              }}
            >
              {tx('soaFlow.excludeConfirm')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setExcluding(false)}>
              {tx('soaFlow.cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
